// Блок «Сейчас нет в продаже» (ТЗ «реклама по наличию», В1–В3, 29.10.2026).
//
// Человек пришёл по ссылке (реклама, поиск, пост в канале) на позицию, которой нет в списке
// витрины: скрыта, продана без прихода, в архиве. Раньше он видел весь каталог с верха без
// единого слова. Теперь — блок наверху каталога: название и фото, «Сейчас нет в продаже»,
// лента «Есть сегодня» — похожие, которые можно купить; под блоком раздел этого товара.
//
// Здесь — чистые правила (что считать «можно купить», какие похожие, как назвать позицию)
// и загрузка сведений о позиции. Сам блок — components/UnavailableNotice.jsx, вызывают его
// MenuPage и BouquetCatalog.
import { api } from '../api';

// Служебная пометка склада в названии — как INTERNAL_TAG в backend/app/services/product_names.py
const INTERNAL_TAG = /\s*[[(]\s*(composition|композиция|букет)\s*[\])]/gi;
// Как _KIND_MODIFIERS в backend/app/services/flowwow_catalog.py
const KIND_MODIFIERS = ['кустов', 'пионовидн', 'садов', 'французск', 'голландск', 'местн', 'одноголов', 'веточн', 'мини', 'микс'];

// Вид цветка по первому существительному без окончания — зеркало flower_kind() на сервере
// (services/flowwow_catalog.py): «Каллы бордо» → «калл», «Кустовая роза» → «роз».
// Это и есть «та же подборка»: подборки собираются по началу названия.
export function flowerKind(name) {
  const words = String(name || '')
    .replace(INTERNAL_TAG, ' ')
    .toLowerCase()
    .split(/[^а-яёa-z0-9]+/)
    .filter(Boolean);
  if (!words.length) return null;
  let w = words[0];
  if (words.length > 1 && KIND_MODIFIERS.some(m => w.startsWith(m))) w = words[1];
  return w.length > 3 && 'аяыиь'.includes(w[w.length - 1]) ? w.slice(0, -1) : w;
}

// Тот же цветок: по полю «Цветок» карточки (сервер отдаёт его у каждого товара: ручная
// поправка или угаданный по названию), без поля — по первому слову названия. Сравниваем
// основы: незнакомый вид сервер отдаёт первым словом как есть («анемона» / «анемоны»)
function sameFlower(p, { name, flower }) {
  if (flower && p.flower) return flowerKind(p.flower) === flowerKind(flower);
  const kind = name ? flowerKind(name) : null;
  return !!kind && flowerKind(p.name) === kind;
}

// Название для людей: служебные пометки долой, «Каллы желтые [70 см]» → «Каллы желтые, 70 см»
export function displayName(name) {
  return String(name || '')
    .replace(INTERNAL_TAG, '')
    .replace(/\s*\[([^\]]+)\]/g, ', $1')
    .replace(/\s+/g, ' ')
    .trim();
}

// Букет: название для Flowwow («Банка с каллой и гладиолусом.N71» — без хвоста «.N71»);
// без него название-номер («63.», «02. [мини]») → «Букет № 63»
export function bouquetDisplayName(name, flowwowName) {
  const fw = String(flowwowName || '').replace(/\s*[.,]\s*N\s*\d+\s*$/, '').trim();
  if (fw) return fw;
  const m = String(name || '').trim().match(/^№?\s*(\d+)\.?(\s*\[[^\]]*\])?$/);
  return m ? `Букет № ${m[1]}` : displayName(name);
}

// Можно купить сегодня: лежит у нас хотя бы на одну пачку (истёкшие витрина уже обнулила)
export function inStockToday(product) {
  const step = product.step || 1;
  return !product.is_expired && (product.quantity || 0) >= step;
}

// Можно заказать хоть на какую-то дату: сегодня или с ближайшей плановой поставки.
// Нет ни того, ни другого — карточка в списке есть, а «В корзину» молча не нажимается:
// вместо неё окно «Сейчас нет в продаже».
export function isProductOrderable(product) {
  const step = product.step || 1;
  if (inStockToday(product)) return true;
  // Как подпись «С 4 ноября: N шт» в ProductCard: любая плановая хотя бы на пачку
  return (product.future_stock || []).some(fs => (fs.available || 0) >= step);
}

const byStock = (a, b) => (b.quantity || 0) - (a.quantity || 0);

export const SIMILAR_MIN = 4;
export const SIMILAR_MAX = 6;

// «Есть сегодня» для товара: сначала тот же цветок (из любого раздела — как подборка), потом
// тот же раздел, по запасу. Меньше SIMILAR_MIN — добираем из остального каталога по запасу.
// Без названия и раздела (неверный адрес) — просто самое ходовое из наличия.
export function pickSimilarProducts(products, { name = null, flower = null, categoryId = null, excludeId = null } = {}) {
  const pool = (products || []).filter(p => p.id !== excludeId && inStockToday(p));
  const sameKind = name || flower ? pool.filter(p => sameFlower(p, { name, flower })).sort(byStock) : [];
  const taken = new Set(sameKind.map(p => p.id));
  const sameCategory = categoryId != null
    ? pool.filter(p => !taken.has(p.id) && (p.category && p.category.id) === categoryId).sort(byStock)
    : [];
  let out = [...sameKind, ...sameCategory];
  if (out.length < SIMILAR_MIN) {
    const have = new Set(out.map(p => p.id));
    out = out.concat(pool.filter(p => !have.has(p.id)).sort(byStock));
  }
  return out.slice(0, SIMILAR_MAX);
}

// Сырое значение сервера, а не с учётом корзины: иначе букет пропадал бы из окна, как только
// его положили в корзину
const bouquetToday = b => b.today_available || 0;

// «Есть сегодня» для букета: собрать сегодня можно — сначала тот же раздел, потом остальные
export function pickSimilarBouquets(bouquets, { categoryId = null, excludeId = null } = {}) {
  const pool = (bouquets || []).filter(b => b.id !== excludeId && bouquetToday(b) > 0);
  const order = (a, b) => bouquetToday(b) - bouquetToday(a);
  const same = categoryId != null
    ? pool.filter(b => (b.category_id != null ? b.category_id : b.category && b.category.id) === categoryId).sort(order)
    : [];
  let out = same;
  if (out.length < SIMILAR_MIN) {
    const have = new Set(out.map(b => b.id));
    out = out.concat(pool.filter(b => !have.has(b.id)).sort(order));
  }
  return out.slice(0, SIMILAR_MAX);
}

// Поровну из нескольких списков по очереди: [[a1,a2],[b1]] → [a1,b1,a2] (похожие из других подборок)
export function interleave(lists, limit = SIMILAR_MAX) {
  const out = [];
  const seen = new Set();
  for (let i = 0; out.length < limit; i++) {
    let any = false;
    for (const list of lists) {
      const x = list[i];
      if (x === undefined) continue;
      any = true;
      if (!seen.has(x.id) && out.length < limit) {
        seen.add(x.id);
        out.push(x);
      }
    }
    if (!any) break;
  }
  return out;
}

// Сведения о позиции для блока: { state: 'ok' | 'archived' | 'missing', name, image, category,
// flower (товар), flowwowName (букет) }.
// 'missing' — такой позиции нет (404); при сбое сети — 'ok' без названия: окно всё равно
// честнее молчаливого каталога. Запрос один на позицию — его начинают ещё до загрузки каталога.
const itemInfo = new Map();

export function loadItemInfo(kind, id) {
  const key = `${kind}:${id}`;
  if (!itemInfo.has(key)) {
    itemInfo.set(key, api.getItemCard(kind, id)
      .then(card => ({ state: 'ok', ...pickCard(card) }))
      .catch(err => {
        if (err && err.status === 410) return { state: 'archived', ...pickCard(err.body || {}) };
        if (err && err.status === 404) return { state: 'missing' };
        return { state: 'ok', name: null, image: null, category: null, flower: null, flowwowName: null };
      }));
  }
  return itemInfo.get(key);
}

function pickCard(card) {
  return {
    name: card.name || null,
    image: card.image || null,
    category: card.category && card.category.id != null ? card.category : null,
    flower: card.flower || null,
    flowwowName: card.flowwow_name || null,
  };
}
